import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Bell, BellOff, Smartphone, Share, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
    pushSupported, permissionState, currentSubscription,
    enablePush, disablePush, sendTestPush,
    canInstallPwa, promptInstallPwa, isIos, isStandalone,
} from "../lib/push";

/** Push notifications + PWA install controls for the profile page.
 *  Works for every role (ansatt + kunde) – the button just prompts the OS
 *  for permission and registers the browser to receive push. */
export default function PushSettings() {
    const [subscribed, setSubscribed] = useState(false);
    const [perm, setPerm] = useState("default");
    const [busy, setBusy] = useState(false);
    const [installOpen, setInstallOpen] = useState(false);
    const [pwaInstallable, setPwaInstallable] = useState(false);
    const supported = pushSupported();
    const iosStandalone = isIos() && isStandalone();
    const iosBrowser = isIos() && !isStandalone();

    const refresh = async () => {
        setPerm(permissionState());
        const sub = await currentSubscription();
        setSubscribed(!!sub);
    };

    useEffect(() => {
        refresh();
        // Detect the beforeinstallprompt fired before this component mounted.
        setPwaInstallable(canInstallPwa());
        const t = setInterval(() => setPwaInstallable(canInstallPwa()), 2000);
        return () => clearInterval(t);
    }, []);

    const doEnable = async () => {
        setBusy(true);
        try {
            await enablePush();
            toast.success("Push-varsler aktivert på denne enheten");
            await refresh();
        } catch (e) {
            toast.error(e.message || "Kunne ikke aktivere push");
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const doDisable = async () => {
        setBusy(true);
        try {
            await disablePush();
            toast.success("Push-varsler deaktivert");
            await refresh();
        } catch {
            toast.error("Kunne ikke deaktivere");
        } finally {
            setBusy(false);
        }
    };

    const doTest = async () => {
        setBusy(true);
        try {
            const { data } = await sendTestPush();
            if (data.sent > 0) {
                toast.success(`Testvarsel sendt til ${data.sent} enhet(er)`);
            } else {
                toast.error("Ingen enheter er registrert – aktiver først.");
            }
        } catch {
            toast.error("Kunne ikke sende test");
        } finally {
            setBusy(false);
        }
    };

    const doInstall = async () => {
        try {
            const ok = await promptInstallPwa();
            if (ok) toast.success("Appen er installert");
            setPwaInstallable(canInstallPwa());
        } catch {
            toast.error("Installasjonen ble avbrutt");
        }
    };

    // --- unsupported (very old browser) ---
    if (!supported) {
        return (
            <Card className="p-5" data-testid="push-settings-unsupported">
                <div className="flex items-start gap-3">
                    <BellOff className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                        <div className="font-semibold text-[#0C1824]">Push-varsler</div>
                        <p className="text-sm text-[#2B3F4D] mt-1">
                            Denne nettleseren støtter dessverre ikke push-varsler.
                            Prøv Chrome, Edge, Firefox eller Safari 16.4+.
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-5" data-testid="push-settings-root">
            <div className="flex items-start gap-3">
                {subscribed ? (
                    <Bell className="w-5 h-5 text-emerald-600 mt-0.5" />
                ) : (
                    <Bell className="w-5 h-5 text-[#BD984D] mt-0.5" />
                )}
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-[#0C1824]">Push-varsler</div>
                        {subscribed && (
                            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" /> Aktivert
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-[#2B3F4D] mt-1">
                        Få varsler direkte på telefonen når det skjer noe viktig
                        (avvik, bookinger, meldinger, sesongoverganger m.m.).
                        Antall uleste vises som badge på app-ikonet.
                    </p>

                    {/* iOS in Safari (not installed) – must add to home screen first */}
                    {iosBrowser && (
                        <div
                            data-testid="push-ios-install-hint"
                            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-[#0C1824]"
                        >
                            <div className="font-semibold flex items-center gap-2">
                                <Smartphone className="w-4 h-4" /> iPhone / iPad – legg til på hjem-skjerm først
                            </div>
                            <ol className="list-decimal list-inside mt-1 space-y-0.5 text-[#2B3F4D]">
                                <li>Trykk på <Share className="inline w-3.5 h-3.5 mx-0.5 align-text-bottom" /> «Del»-ikonet nederst i Safari.</li>
                                <li>Velg <strong>«Legg til på Hjem-skjerm»</strong>.</li>
                                <li>Åpne appen fra hjem-skjermen og aktiver varsler her.</li>
                            </ol>
                        </div>
                    )}

                    {/* Android / desktop – native install prompt available */}
                    {pwaInstallable && (
                        <div className="mt-3 rounded-lg border border-[#BD984D]/30 bg-[#A57F39]/5 p-3 text-sm">
                            <div className="font-semibold text-[#0C1824] flex items-center gap-2">
                                <Plus className="w-4 h-4" /> Installer Canal Boats-appen
                            </div>
                            <p className="text-[#2B3F4D] mt-1">
                                Installer for full skjermmodus, hurtigstart og pålitelige push-varsler.
                            </p>
                            <Button
                                data-testid="pwa-install-btn"
                                onClick={doInstall}
                                className="mt-2 bg-[#A57F39] hover:bg-[#BD984D] text-white"
                                size="sm"
                            >
                                Installer app
                            </Button>
                        </div>
                    )}

                    {perm === "denied" && (
                        <div
                            data-testid="push-permission-denied"
                            className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                        >
                            Varsler er blokkert for denne siden. Skru dem på i
                            nettleser-innstillingene (låsen ved adresselinja) og
                            last siden på nytt.
                        </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                        {!subscribed ? (
                            <Button
                                data-testid="push-enable-btn"
                                onClick={doEnable}
                                disabled={busy || perm === "denied" || iosBrowser}
                                className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                            >
                                <Bell className="w-4 h-4 mr-2" />
                                Aktiver push-varsler
                            </Button>
                        ) : (
                            <>
                                <Button
                                    data-testid="push-test-btn"
                                    onClick={doTest}
                                    disabled={busy}
                                    variant="outline"
                                >
                                    Send testvarsel
                                </Button>
                                <Button
                                    data-testid="push-disable-btn"
                                    onClick={doDisable}
                                    disabled={busy}
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <BellOff className="w-4 h-4 mr-2" />
                                    Deaktiver
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}
